import gsap from "gsap"
import CustomEase from "gsap/CustomEase"
import Lenis from "lenis"

gsap.registerPlugin(CustomEase)

export default class Passenger {
    constructor(options = {}) {
        this.buttons = options.buttons || document.querySelectorAll(".open-passenger")
        this.lightboxLenis = null
        this.scroll = options.scroll
        this.sidebarTl = null
        this.isSidebarOpen = false
        this.data = window.passengerData || null
        
        this.init()
    }

    isMobile() {
        return window.innerWidth <= 1024 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }

    init() {
        if (!this.buttons || this.buttons.length === 0) return
        
        this.buttons.forEach(button => {
            button.addEventListener("click", (e) => {
                e.preventDefault()
                this.openLightbox()
            })
        })
    }

    openLightbox() {
        if (!this.data) {
            console.error('Passenger data not found')
            return
        }
        
        this.createLightboxElement()
        this.animateLightboxOpen()
        this.renderContent(this.data)
        this.initLightboxScroll()
        this.attachCloseHandlers()
        this.attachNavigationHandlers()
    }

    createLightboxElement() {
        const lightbox = document.createElement("div")
        lightbox.className = "lightbox"
        lightbox.innerHTML = `
            <div class="lightbox__overlay"></div>
            <div class="lightbox__content">
                <div class="lightbox__loader">
                    
                </div>
            </div>
        `
        document.body.appendChild(lightbox)
        
        if (this.isMobile()) {
            // Mobile: Lock body scroll - save scroll position
            this.scrollPosition = window.pageYOffset
            document.body.style.position = 'fixed'
            document.body.style.top = `-${this.scrollPosition}px`
            document.body.style.width = '100%'
        } else {
            // Desktop: Use Lenis stop
            document.body.style.overflow = "hidden"
            if (this.scroll) {
                this.scroll.stop()
            }
        }
    }

    animateLightboxOpen() {
        gsap.fromTo(".lightbox__overlay", 
            { 
                scaleY: 0,
                transformOrigin: "bottom"
            },
            { 
                scaleY: 1,
                duration: 0.8,
                ease: "power3.out"
            }
        )
        
        gsap.fromTo(".lightbox__content",
            {
                y: "100%",
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                ease: "power3.out",
                delay: 0.2
            }
        )
    }

    renderContent(data) {
        const lightboxContent = document.querySelector(".lightbox__content")
        const baseUrl = window.passengerBaseUrl || ''
        
        // Build sections HTML from local data
        const sectionsHTML = data.sections.map((section) => `
            <section class="lightbox__section" id="section-${section.id}">
                <h2 class="title title--dark">${section.title}</h2>
                <div class="lightbox__body" data-split="heading">
                    ${section.body.map(item => item.replace(/src="images\//g, `src="${baseUrl}/public/images/`)).join('')}
                </div>
            </section>
        `).join('')
        
        // Build navigation list
        const navListHTML = data.sections.map((section) => `
            <li><a href="#section-${section.id}">${section.title}</a></li>
        `).join('')
        
        // Build Finca links
        const fincaLinksHTML = data.finca_links.map(item => `
            <li><a href="${item.url}" target="${item.target}">${item.title} <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="1.86133" y1="3.49878" x2="7.86133" y2="3.49878" stroke="white" stroke-width="1.25"/>
                <line x1="2.35933" y1="8.34541" x2="6.60197" y2="4.10277" stroke="white" stroke-width="1.25"/>
                <line x1="7.23633" y1="8.87573" x2="7.23633" y2="2.87573" stroke="white" stroke-width="1.25"/>
            </svg></a></li>
        `).join('')
        
        // Build Club links
        const clubLinksHTML = data.club_links.map(item => `
            <li><a href="${item.url}" target="${item.target}">${item.title} <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="1.86133" y1="3.49878" x2="7.86133" y2="3.49878" stroke="white" stroke-width="1.25"/>
                <line x1="2.35933" y1="8.34541" x2="6.60197" y2="4.10277" stroke="white" stroke-width="1.25"/>
                <line x1="7.23633" y1="8.87573" x2="7.23633" y2="2.87573" stroke="white" stroke-width="1.25"/>
            </svg></a></li>
        `).join('')
        
        lightboxContent.innerHTML = `
            <div class="lightbox__header">
                <div class="lightbox__header__navigation">
                    <div class="lightbox__header__navigation__trigger">
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line y1="7.17065" x2="13.4267" y2="7.17065" stroke="white" stroke-width="1.5"/>
                            <line x1="6.6709" y1="13.9268" x2="6.6709" y2="0.500071" stroke="white" stroke-width="1.5"/>
                        </svg>
                        <span>Navigation</span>
                    </div>
                    <div class="lightbox__header__navigation__sidebar">
                        <ul class="lightbox__sidebar__content__list">
                            ${navListHTML}
                        </ul>
                        <div style="display: flex; gap: 1rem;">
                            <div style="flex: 1 1 50%;">
                                <h5>Finca La Caminera</h5>
                                <ul class="lightbox__sidebar__content__external">
                                    ${fincaLinksHTML}
                                </ul>
                            </div>
                            <div style="flex: 1 1 50%;">
                                <h5>La Caminera Country Club</h5>
                                <ul class="lightbox__sidebar__content__external">
                                    ${clubLinksHTML}
                                </ul>
                            </div>
                        </div>

                        <div class="lightbox__sidebar__content__actions">
                            <p>${data.contact.phone} <br /> ${data.contact.email}</p>

                            <a href="${data.contact.whatsapp}" target="_blank" aria-label="Open whatsapp" class="button button--icon">
                                <div class="button__bg"></div>
                                <span data-button-animate-chars="" class="button__text">Whatsapp</span>
                                <div class="button__arrow">
                                    <svg width="8" height="13" viewBox="0 0 8 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <line x1="1.41021" y1="1.40033" x2="6.92439" y2="6.91451" stroke="currentColor" stroke-width="1.5"/>
                                        <line x1="0.835393" y1="11.9423" x2="6.34957" y2="6.42815" stroke="currentColor" stroke-width="1.5"/>
                                    </svg>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
                <button class="lightbox__close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" stroke-width="2"/>
                        <line x1="22" y1="2" x2="2" y2="22" stroke="currentColor" stroke-width="2"/>
                    </svg>
                </button>
            </div>
            <div class="lightbox__inner">
                ${sectionsHTML}
            </div>
            <div class="lightbox__sidebar">
                <img src="${baseUrl}/public/${data.image}" alt="${data.title}" />

                <div class="lightbox__sidebar__content">
                    <div>
                        <h5>Exclusive Services</h5>
                        <ul class="lightbox__sidebar__content__list">
                            ${navListHTML}
                        </ul>
                    </div>

                    <div>
                        <div style="display: flex; gap: 1rem;">
                            <div style="flex: 1 1 50%;">
                                <h5>Finca La Caminera</h5>
                                <ul class="lightbox__sidebar__content__external">
                                    ${fincaLinksHTML}
                                </ul>
                            </div>

                            <div style="flex: 1 1 50%;">
                                <h5>La Caminera Country Club</h5>
                                <ul class="lightbox__sidebar__content__external">
                                    ${clubLinksHTML}
                                </ul>
                            </div>
                        </div>

                        <div class="lightbox__sidebar__content__actions">
                            <p>
                                <a href="tel:+34695386666" target="_blank">${data.contact.phone}</a> <br />
                                <a href="mailto:opsmanager@fincalacaminera.com" target="_blank">${data.contact.email}</a>
                            </p>

                            <a href="${data.contact.whatsapp}" target="_blank" aria-label="Open whatsapp" class="button button--icon">
                                <span data-button-animate-chars="" class="button__text">Whatsapp</span>
                                <div class="button__arrow">
                                    <svg width="8" height="13" viewBox="0 0 8 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <line x1="1.41021" y1="1.40033" x2="6.92439" y2="6.91451" stroke="currentColor" stroke-width="1.5"/>
                                        <line x1="0.835393" y1="11.9423" x2="6.34957" y2="6.42815" stroke="currentColor" stroke-width="1.5"/>
                                    </svg>
                                </div>
                            </a>
                        </div>
                    </div>      
                </div>
            </div>
        `
        
        gsap.from(".lightbox__inner, .lightbox__sidebar", {
            y: 30,
            opacity: 0,
            duration: 0.5,
            stagger: 0.2,
            ease: "power2.out"
        })
        
        this.setupSidebarAnimation()
        this.attachSidebarHandlers()
    }

    setupSidebarAnimation() {
        const sidebar = document.querySelector('.lightbox__header__navigation__sidebar')
        if (!sidebar) return

        this.sidebarTl = gsap.timeline({ paused: true })
        
        // Set initial hidden state
        gsap.set(sidebar, { display: 'none', scaleY: 0, transformOrigin: "top" })
        gsap.set('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__list li', { y: 20, autoAlpha: 0 })
        gsap.set('.lightbox__header__navigation__sidebar .lightbox__header__navigation__sidebar h5', { y: 20, autoAlpha: 0 })
        gsap.set('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__external li', { y: 20, autoAlpha: 0 })
        gsap.set('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__actions', { y: 20, autoAlpha: 0 })
        
        // Build the animation timeline
        this.sidebarTl.set(sidebar, { display: 'block' })
        
        this.sidebarTl.to(sidebar, {
            duration: .8,
            scaleY: 1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        })
        
        this.sidebarTl.to('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__list li', {
            duration: 0.5,
            y: 0,
            autoAlpha: 1,
            stagger: 0.05,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.6')
        
        this.sidebarTl.to('.lightbox__header__navigation__sidebar .lightbox__header__navigation__sidebar h5', {
            duration: 0.4,
            y: 0,
            autoAlpha: 1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.4')
        
        this.sidebarTl.to('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__external li', {
            duration: 0.5,
            y: 0,
            autoAlpha: 1,
            stagger: 0.05,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.4')
        
        this.sidebarTl.to('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__actions', {
            duration: 0.4,
            y: 0,
            autoAlpha: 1,
            ease: CustomEase.create("out", "0.125, 0.425, 0.270, 1.000")
        }, '-=0.4')
    }

    attachSidebarHandlers() {
        const trigger = document.querySelector('.lightbox__header__navigation__trigger')
        if (!trigger) return
        
        trigger.addEventListener('click', (e) => {
            e.preventDefault()
            this.toggleSidebar()
        })
        
        // Close sidebar when clicking a navigation link
        const navLinks = document.querySelectorAll('.lightbox__header__navigation__sidebar .lightbox__sidebar__content__list a')
        navLinks.forEach(link => {
            link.addEventListener('click', () => {
                if (this.isSidebarOpen) {
                    this.closeSidebar()
                }
            })
        })
    }

    toggleSidebar() {
        if (this.isSidebarOpen) {
            this.closeSidebar()
        } else {
            this.openSidebar()
        }
    }

    openSidebar() {
        const trigger = document.querySelector('.lightbox__header__navigation__trigger')
        this.isSidebarOpen = true
        if (trigger) trigger.classList.add('is-active')
        if (this.sidebarTl) this.sidebarTl.play()
    }

    closeSidebar() {
        const trigger = document.querySelector('.lightbox__header__navigation__trigger')
        const sidebar = document.querySelector('.lightbox__header__navigation__sidebar')
        
        this.isSidebarOpen = false
        if (trigger) trigger.classList.remove('is-active')
        
        gsap.to(sidebar, {
            duration: 0.3,
            ease: 'power2.in',
            autoAlpha: 0,
            onComplete: () => {
                if (this.sidebarTl) this.sidebarTl.pause(0)
                gsap.set(sidebar, { display: 'none', autoAlpha: 1 })
            }
        })
    }

    initLightboxScroll() {
        const lightboxInner = document.querySelector(".lightbox__inner")
        this.lightboxLenis = new Lenis({
            wrapper: lightboxInner,
            content: lightboxInner,
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            wheelMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
            infinite: false,
        })
        
        const rafLightbox = (time) => {
            if (this.lightboxLenis) {
                this.lightboxLenis.raf(time)
                requestAnimationFrame(rafLightbox)
            }
        }
        requestAnimationFrame(rafLightbox)
    }

    attachCloseHandlers() {
        const closeButton = document.querySelector(".lightbox__close")
        const overlay = document.querySelector(".lightbox__overlay")
        
        if (closeButton) {
            closeButton.addEventListener("click", () => this.closeLightbox())
        }
        
        if (overlay) {
            overlay.addEventListener("click", () => this.closeLightbox())
        }
    }

    attachNavigationHandlers() {
        const navLinks = document.querySelectorAll(".lightbox__sidebar__content__list a")
        
        navLinks.forEach(link => {
            link.addEventListener("click", (e) => {
                e.preventDefault()
                
                const sectionId = link.getAttribute("href")
                const section = document.querySelector(sectionId)
                
                if (section && this.lightboxLenis) {
                    navLinks.forEach(l => l.classList.remove("active"))
                    link.classList.add("active")

                    this.lightboxLenis.scrollTo(section, {
                        offset: -20,
                        duration: 1.2,
                        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
                    })
                }
            })
        })
        
        this.initScrollSpy()
    }

    initScrollSpy() {
        if (!this.lightboxLenis) return
        
        const navLinks = document.querySelectorAll(".lightbox__sidebar__content__list a")
        const sections = document.querySelectorAll(".lightbox__section")
        
        this.lightboxLenis.on("scroll", ({ scroll }) => {
            let currentSection = null
            
            sections.forEach(section => {
                const sectionTop = section.offsetTop
                const sectionHeight = section.offsetHeight
                
                if (scroll >= sectionTop - 100 && scroll < sectionTop + sectionHeight - 100) {
                    currentSection = section.id
                }
            })
            
            if (currentSection) {
                navLinks.forEach(link => {
                    const href = link.getAttribute("href").substring(1)
                    if (href === currentSection) {
                        link.classList.add("active")
                    } else {
                        link.classList.remove("active")
                    }
                })
            }
        })
    }

    closeLightbox() {
        if (this.lightboxLenis) {
            this.lightboxLenis.destroy()
            this.lightboxLenis = null
        }
        
        gsap.to(".lightbox__overlay", {
            scaleY: 0,
            transformOrigin: "bottom",
            duration: 0.6,
            ease: "power3.in"
        })
        
        gsap.to(".lightbox__content", {
            y: "100%",
            opacity: 0,
            duration: 0.6,
            ease: "power3.in",
            onComplete: () => {
                document.querySelector(".lightbox")?.remove()
                
                if (this.isMobile()) {
                    // Mobile: Unlock body scroll - restore scroll position
                    document.body.style.position = ''
                    document.body.style.top = ''
                    document.body.style.width = ''
                    window.scrollTo(0, this.scrollPosition)
                } else {
                    // Desktop: Restore Lenis
                    document.body.style.overflow = ""
                    if (this.scroll) {
                        this.scroll.start()
                    }
                }
            }
        })
    }
}
